import {
  CButton,
  COUNTRIES,
  FormGroup,
  disableButton,
  handleNavigationStatus,
} from '@maw/utility';
import {
  ActionFunction,
  ActionFunctionArgs,
  Form,
  LoaderFunction,
  LoaderFunctionArgs,
  useActionData,
  useNavigation,
  useParams,
} from 'react-router-dom';
import Card from '../../../Card';
import { getById, queryClient, upsertResource } from '../../../../util/request';
import Container from '../../../Container';
import Alert from '../../../Alert';
import { gender, permitStatus } from '../../../../util/constant';
import { disableWhenLoading } from '../../../../util/helper';
import { useRedirect } from '../../../../hooks/redirect';
import { User } from '../../../../types/user';
import { useQuery } from '@tanstack/react-query';
import Avatar from '../../../Avatar';
import useOperation from '../../../../hooks/operation';
import Modal from '../../../Modal';
import HiddenInput from '../../../HiddenInput';

export default function ProfileSettings() {
  const navigation = useNavigation();
  const params = useParams();
  const actionData: any = useActionData();
  const { data } = useQuery({
    queryKey: ['users', params.id],
    queryFn: ({ signal }) =>
      getById({ signal, resource: `users/${params.id}/profile` }),
  });
  const user: User = data?.data;

  useRedirect(actionData, 'users', true);

  const { id, show, handleDelete, markObjectForDeletion, closeModal } =
    useOperation();

  return (
    <Container>
      <Alert data={actionData} />
      <Modal
        show={show}
        onClose={closeModal}
        onAction={() =>
          handleDelete(`users/${id}/images/delete`, ['users', id])
        }
      />
      {user && user.avatar && (
        <Avatar
          className='mb-4'
          imageUrl={user.avatar}
          onDelete={() => markObjectForDeletion(user.id)}
        />
      )}
      <Container.Heading>User Profile</Container.Heading>
      <Container.SubHeading>
        Authorized user can write the file.
      </Container.SubHeading>
      {user && (
        <Card>
          <Card.Header>{`${user.firstName} ${user.lastName}`}</Card.Header>
          <Form method='post' encType='multipart/form-data' noValidate>
            <FormGroup>
              <div className='flex flex-col gap-x-2 md:flex-row md:space-x-2'>
                <FormGroup.Input
                  labelclassname='bg-gray-100'
                  className='flex-1'
                  type='text'
                  id='first_name'
                  title='First name'
                  placeholder='Enter first name'
                  defaultValue={user.firstName}
                  data={actionData}
                  disabled={disableWhenLoading(navigation)}
                />
                <FormGroup.Input
                  labelclassname='bg-gray-100'
                  className='flex-1'
                  type='text'
                  id='last_name'
                  title='Last name'
                  placeholder='Enter last name'
                  defaultValue={user.lastName}
                  data={actionData}
                  disabled={disableWhenLoading(navigation)}
                />
              </div>
              <FormGroup.Input
                labelclassname='bg-gray-100'
                className='flex-1'
                type='file'
                id='avatar'
                title='Avatar (MaxHeight: 500, MaxWidth: 500)'
                accept='.jpeg, .png, .jpg, .svg'
                data={actionData}
                disabled={disableWhenLoading(navigation)}
              />
              <div className='flex flex-col gap-x-2 md:flex-row md:space-x-2'>
                <FormGroup.Input
                  labelclassname='bg-gray-100'
                  className='flex-1'
                  type='text'
                  id='address'
                  title='Address'
                  placeholder='Enter your address'
                  defaultValue={user.address}
                  data={actionData}
                  disabled={disableWhenLoading(navigation)}
                />
                <FormGroup.Input
                  labelclassname='bg-gray-100'
                  className='flex-1'
                  type='text'
                  id='city'
                  title='City'
                  placeholder='Enter your city'
                  defaultValue={user.city}
                  data={actionData}
                  disabled={disableWhenLoading(navigation)}
                />
              </div>
              <div className='flex flex-col gap-x-2 md:flex-row md:space-x-2'>
                <FormGroup.Input
                  labelclassname='bg-gray-100'
                  className='flex-1'
                  type='text'
                  id='postal_code'
                  title='Postal code'
                  placeholder='Enter the postal code'
                  defaultValue={user.postalCode}
                  data={actionData}
                  disabled={disableWhenLoading(navigation)}
                />
                <FormGroup.Input
                  labelclassname='bg-gray-100'
                  className='flex-1'
                  type='text'
                  id='telephone'
                  title='Telephone'
                  placeholder='e.g +35811200019'
                  defaultValue={user.telephone}
                  data={actionData}
                  disabled={disableWhenLoading(navigation)}
                />
              </div>
              <div className='flex flex-col gap-x-2 md:flex-row md:space-x-2'>
                <FormGroup.Select
                  labelclassname='bg-gray-100'
                  className='flex-1'
                  id='country_of_origin'
                  title='Country of origin'
                  defaultValue={user.countryOfOrigin}
                  data={actionData}
                  disabled={disableWhenLoading(navigation)}
                >
                  {COUNTRIES.map((country) => (
                    <option value={country.value} key={country.value}>
                      {country.value}
                    </option>
                  ))}
                </FormGroup.Select>
                <FormGroup.Select
                  labelclassname='bg-gray-100'
                  className='flex-1'
                  id='country_of_residence'
                  title='Country of residence'
                  defaultValue={user.countryOfResidence}
                  data={actionData}
                  disabled={disableWhenLoading(navigation)}
                >
                  {COUNTRIES.map((country) => (
                    <option value={country.value} key={country.value}>
                      {country.value}
                    </option>
                  ))}
                </FormGroup.Select>
              </div>
              <div className='flex flex-col gap-x-2 md:flex-row md:space-x-2'>
                <FormGroup.Select
                  labelclassname='bg-gray-100'
                  className='flex-1'
                  id='gender'
                  title='Gender'
                  defaultValue={user.gender}
                  data={actionData}
                  disabled={disableWhenLoading(navigation)}
                >
                  {gender.map((gender) => (
                    <option value={gender.value} key={gender.value}>
                      {gender.name}
                    </option>
                  ))}
                </FormGroup.Select>
              </div>
              <div className='flex flex-col gap-x-2 md:flex-row md:space-x-2'>
                <FormGroup.Select
                  labelclassname='bg-gray-100'
                  className='flex-1'
                  id='permit_status'
                  title='Permit status'
                  defaultValue={user.permitStatus}
                  data={actionData}
                  disabled
                >
                  {permitStatus.map((permit) => (
                    <option value={permit.value} key={permit.value}>
                      {permit.name}
                    </option>
                  ))}
                </FormGroup.Select>
              </div>
              <HiddenInput />
              <CButton
                rounded
                variant='primary'
                type='submit'
                disabled={disableButton(navigation)}
                className='mt-6'
              >
                {handleNavigationStatus(navigation, 'Submit')}
              </CButton>
            </FormGroup>
          </Form>
        </Card>
      )}
    </Container>
  );
}

export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderFunction> {
  return await queryClient.fetchQuery({
    queryKey: ['users', params.id],
    queryFn: ({ signal }) =>
      getById({ signal, resource: `users/${params.id}/profile` }),
  });
}

export async function action({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionFunction> {
  await queryClient.invalidateQueries({ queryKey: ['users'] });
  return await upsertResource({
    request,
    resource: `users/${params.id}/profile/update`,
    isFormData: true,
    action: 'update',
  });
}
